"use client";

import type {
  LeadFormFieldChangeHandler,
  MultiSelectOption,
  MultiSelectStepInput,
} from "./form-types";
import type { HeroVariantStyles } from "./hero-variant-styles";
import type { StepInputRendererProps } from "./StepInputRenderer";

import { cn } from "@homeinstead/ui";
import { Button } from "@homeinstead/ui/components/Button";
import {
  resolveFieldsetLegendClasses,
  resolveLabelFontFamilyClass,
  resolveTextSizeClass,
} from "./form-item-styles";
import { defaultHeroVariantStyles } from "./hero-variant-styles";
import { MultiSelectCheckboxCardOption } from "./MultiSelectCheckboxCardOption";
import { MultiSelectCheckboxOption } from "./MultiSelectCheckboxOption";
import {
  resolveOptionValue,
  usePersistedMultiSelectState,
} from "./use-persisted-multi-select-state";

export interface MultiSelectInputInnerProps {
  input: MultiSelectStepInput & {
    options: MultiSelectOption[];
  };
  errors: StepInputRendererProps["errors"];
  /**
   * Called when the Continue button is clicked. Receives the resolved label for
   * each selected option (metricsLabel ?? value ?? label).
   */
  onContinue?: (selectedOptions: string[]) => void;
  /**
   * Called on every checkbox toggle so the XState machine can persist the
   * selection into persistedFieldValues. Name is "fieldName.optionValue",
   * value is "on" when checked and "" when unchecked.
   * Only called when fieldName is non-null.
   */
  onFieldChange?: LeadFormFieldChangeHandler;
  /** Hero-mode contrast styles from the step info panel Visual Variant field. */
  heroVariantStyles?: HeroVariantStyles;
}

export function MultiSelectInputInner({
  input,
  errors,
  onContinue,
  onFieldChange,
  heroVariantStyles,
}: MultiSelectInputInnerProps) {
  const {
    buttonLabel,
    fieldName,
    fontWeight,
    label,
    helperText,
    variant,
    minSelected,
    maxSelected,
    options,
    selectionIndicator,
    textSize,
  } = input;

  const helperTextSize =
    "helperTextSize" in input
      ? (input as typeof input & { helperTextSize?: string | null })
          .helperTextSize
      : undefined;

  const [selected, setOptionSelected] = usePersistedMultiSelectState(
    fieldName,
    options,
    onFieldChange,
  );

  const handleContinue = () => {
    const resolvedOptions = options
      .filter((opt) => selected.has(resolveOptionValue(opt)))
      .map((opt) => opt.metricsLabel ?? opt.value ?? opt.label ?? "");
    onContinue?.(resolvedOptions);
  };

  const effectiveFieldName = fieldName ?? "_multiSelect";
  const resolvedVariant = variant ?? "checkbox-card";
  const atMax = maxSelected != null && selected.size >= maxSelected;

  const fieldError =
    errors?.[effectiveFieldName]?.[0] ?? errors?.["_multiSelect"]?.[0];

  return (
    <fieldset className="flex flex-col gap-3">
      {!label ? null : (
        <legend
          className={resolveFieldsetLegendClasses({
            fontWeight,
            textSize,
          })}
        >
          {label}
        </legend>
      )}
      {helperText && (
        <p
          className={cn(
            "mb-4",
            heroVariantStyles?.helperTextColorClass ??
              defaultHeroVariantStyles.helperTextColorClass,
            resolveLabelFontFamilyClass(),
            resolveTextSizeClass(helperTextSize ?? null, "sm"),
          )}
        >
          {helperText}
        </p>
      )}
      <div
        className={cn(
          "flex flex-col gap-3",
          resolvedVariant === "checkbox-card" && "grid grid-cols-1 gap-3",
        )}
      >
        {options.map((option) => {
          const optionValue = resolveOptionValue(option);
          const isChecked = selected.has(optionValue);
          const isDisabled = atMax && !isChecked;

          const sharedProps = {
            checked: isChecked,
            disabled: isDisabled,
            fieldName: effectiveFieldName,
            onChange: setOptionSelected,
            option,
          };

          return resolvedVariant === "checkbox-card" ? (
            <MultiSelectCheckboxCardOption
              key={option._key}
              selectionIndicator={selectionIndicator}
              {...sharedProps}
            />
          ) : (
            <MultiSelectCheckboxOption key={option._key} {...sharedProps} />
          );
        })}
      </div>
      {fieldError ? (
        <p className="text-red-600 text-sm" role="alert">
          {fieldError}
        </p>
      ) : null}
      {minSelected != null && minSelected > 1 ? (
        <p aria-live="polite" className="text-gray-500 text-xs">
          Select at least {minSelected}
          {maxSelected != null ? ` and up to ${maxSelected}` : ""} options.
        </p>
      ) : null}
      {onContinue != null ? (
        <Button
          onClick={handleContinue}
          type="button"
          variant={heroVariantStyles?.continueButtonVariant}
        >
          {buttonLabel ?? "Continue"}
        </Button>
      ) : null}
    </fieldset>
  );
}
